import moment from 'moment';
import ApiService from './api';

const DialogService = {
  getDialogs: async function(botId: string): Promise<any> {
    try {
      const response = await ApiService.get('/schaltzentrale/dialogs/' + botId);
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * Fetches dialogs filtered by time range and optional intent/filter/channel criteria.
   * @param uniqueBotId ID of the bot
   * @param from Start of the time range
   * @param to End of the time range
   * @param intent Only dialogs matched to this intent (ignored when `unknown` is set)
   * @param filter Additional filter keyword (ignored when `unknown` is set)
   * @param channel Only dialogs from this channel
   * @param unknown When true, only dialogs the bot could not match to an intent
   */
  getFilteredDialogs: async function(
    uniqueBotId: string,
    from: string,
    to: string,
    intent?: string | null,
    filter?: string | null,
    channel?: string | null,
    unknown?: boolean
  ): Promise<any> {
    try {
      const body: Record<string, any> = {
        uniqueBotId,
        from,
        to,
      };

      if (unknown) {
        body.unknown = true;
      } else {
        if (intent) {
          body.intent = intent;
        }

        if (filter) {
          body.filter = filter;
        }
      }

      // Allow channel filtering in any case
      if (channel) {
        body.channel = channel;
      }

      const response = await ApiService.post(`/schaltzentrale/dialogs`, body);
      return response.data;
    } catch (error) {
      return {};
    }
  },
  getChat: async function(uniqueUserId: string, botId: string): Promise<any> {
    try {
      const response = await ApiService.get(
        '/schaltzentrale/dialogs/chat/' + botId + '/' + encodeURIComponent(uniqueUserId)
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * Fetches all chats of a bot for one day.
   * @param botId ID of the bot
   * @param date Any moment-parseable date; defaults to the start of today (ISO string)
   */
  getChats: async function(
    botId: string,
    date: any = moment()
      .startOf('day')
      .toDate()
      .toISOString()
  ): Promise<any> {
    date = moment(date);
    try {
      const response = await ApiService.get(
        '/schaltzentrale/chats/' + botId + '/' + date.format('YYYY-MM-DD')
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  getChatHistory: async function(botId: string, uuid: string): Promise<any> {
    try {
      const response = await ApiService.get(
        '/schaltzentrale/chats/history/' + botId + '/' + encodeURIComponent(uuid)
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * Fetches the K2 (AI pipeline) protocol of a conversation.
   * @param botId ID of the bot
   * @param conversationId ID of the conversation
   * @param fields Restrict the response to these protocol fields; empty for all
   * @returns The protocol result, or null when unavailable/on error
   */
  getK2Protocol: async function(botId: string, conversationId: string, fields: string[] | string = []): Promise<any | null> {
    try {
      const requestedFields = Array.isArray(fields)
        ? fields.filter(Boolean).join(',')
        : fields;
      const query = requestedFields
        ? `?fields=${encodeURIComponent(requestedFields)}`
        : '';
      const response = await ApiService.get(
        `/schaltzentrale/k2/bot/${botId}/protocol/${conversationId}${query}`
      );

      return response.data?.result || null;
    } catch (error) {
      return null;
    }
  },
  deleteConversation: async function(botId: string, uniqueUserId: string): Promise<any> {
    try {
      const response = await ApiService.post(
        `/messages/delete/${botId}/${encodeURIComponent(uniqueUserId)}`
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * Generates (and optionally persists) an AI summary of a user's dialog.
   * @param botId ID of the bot
   * @param uniqueUserId The chat widget user ID
   * @param saveToDatabase Persist the generated summary server-side
   * @param options Extra body fields overriding the defaults (maxMessages, showDetails, ...)
   */
  getDialogSummary: async function(botId: string, uniqueUserId: string, saveToDatabase: boolean = false, options: Record<string, any> = {}): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const response = await ApiService.post(`/schaltzentrale/dialog-summary/bot/${botId}/summary`, {
          maxMessages: 50, // Default value, can be adjusted as needed
          saveToDatabase,
          showDetails: true,
          uniqueUserId: uniqueUserId,
          ...options,
      });

      if (response.data?.status !== 'ok') {
        throw new Error('Invalid response from server');
      }

      return { success: true, data: response.data?.result };
    } catch (error) {
      return { success: false, error };
    }
  },

  // --- Conversation export ---------------------------------------------------

  /**
   * Request an asynchronous conversation export for a time range.
   * The backend processes it in the background and emails the requester when ready.
   */
  requestConversationExport: async function({
    botId,
    from,
    to,
    channelIds = [],
    uniqueUserIds = [],
  }: {
    botId: string;
    from: string;
    to: string;
    channelIds?: string[];
    uniqueUserIds?: string[];
  }): Promise<{ success: boolean; data?: any; error?: any }> {
    const data: Record<string, any> = { uniqueBotId: botId, from, to };
    if (channelIds.length) data.channelIds = channelIds;
    if (uniqueUserIds.length) data.uniqueUserIds = uniqueUserIds;

    try {
      const response = await ApiService.post(`/api/v1/conversation/export`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  /** Lists all conversation exports for a bot. */
  getConversationExports: async function(botId: string): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const response = await ApiService.get(`/api/v1/conversation/exports?botId=${botId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  /** Fetches details (progress, download log, failures) for a single export. */
  getConversationExportDetails: async function(id: string): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const response = await ApiService.get(`/api/v1/conversation/export/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  /**
   * Downloads a completed conversation export as a JSONL file.
   *
   * Prefers the File System Access API save-picker (no request is sent until the
   * user confirms the save dialog); falls back to a regular browser download when
   * `useDefaultBrowserDownload` is set or the picker is unsupported.
   */
  downloadConversationExport: async function({
    id,
    onDownloadStarted,
    useDefaultBrowserDownload = false,
  }: {
    id: string;
    onDownloadStarted?: () => void;
    useDefaultBrowserDownload?: boolean;
  }): Promise<{ success: boolean; canceled?: boolean; filename?: string; error?: any }> {
    const fallbackName = `conversation-export-${id}.jsonl`;

    try {
      // Use default browser download without showing a save dialog
      if (useDefaultBrowserDownload) {
        onDownloadStarted?.();
        const { filename, blob } = await _fetchConversationExportBlob({ id, fallbackName, headers: { filename: fallbackName } });
        return _saveBlob(blob, filename);
      }

      // Picker-first mode: no API request is sent until the user confirms the save dialog.
      const { supported, canceled, fileHandler, error: pickerError } = await _openSaveFilePicker(fallbackName);
      if (canceled) return { success: true, canceled: true, filename: fallbackName }; // User has canceled
      if (!supported) return { success: false, error: new Error('File System Access API is not supported in this browser') };
      if (!fileHandler) throw pickerError || new Error('No file handler obtained from save file picker');

      onDownloadStarted?.();
      const { filename, blob } = await _fetchConversationExportBlob({ id, fallbackName, headers: { filename: fileHandler?.name || fallbackName } });
      const writable = await fileHandler.createWritable();
      await writable.write(blob);
      await writable.close();

      return { success: true, filename };
    } catch (error) {
      return { success: false, error };
    }
  },
};

const _fetchConversationExportBlob = async ({ id, fallbackName, headers = {} }: { id: string; fallbackName: string; headers?: Record<string, string> }) => {
  const response = await ApiService.get(`/api/v1/conversation/export/${id}/download`, {
    responseType: 'blob',
    headers: { Accept: 'application/jsonl, application/x-ndjson, application/ndjson, application/json, text/plain;q=0.9', ...headers },
  });

  if (response.status !== 200) throw new Error('Failed to download conversation export');

  const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
  const filename = _parseFilenameFromDisposition(contentDisposition, fallbackName);
  const contentType = (response.headers?.['content-type'] as string | undefined) || 'application/jsonl;charset=utf-8';
  const responseBlob = response.data as Blob;
  const blob = responseBlob.type ? responseBlob : new Blob([responseBlob], { type: contentType });

  return { filename, blob };
};

// --- Download helpers --------------------------------------------------------

const _openSaveFilePicker = async (filename: string): Promise<{ supported: boolean; saved?: boolean; canceled: boolean; fileHandler?: any; error?: any }> => {
  if (!(window as any).showSaveFilePicker) {
    return { supported: false, saved: false, canceled: false };
  }

  try {
    const fileHandler = await (window as any).showSaveFilePicker({
      suggestedName: filename,
      types: [{
        description: 'Conversation export',
        accept: {
          'application/json': ['.json'],
          'application/jsonl': ['.jsonl'],
          'application/ndjson': ['.ndjson'],
          'application/x-ndjson': ['.ndjson'],
          'text/plain': ['.txt'],
        },
      }],
    });

    return { supported: true, saved: true, canceled: false, fileHandler };
  } catch (error: any) {
    if (error?.name === 'AbortError' || error?.name === 'NotAllowedError') {
      return { supported: true, saved: false, canceled: true };
    }

    return { supported: true, saved: false, canceled: false, error };
  }
};

const _parseFilenameFromDisposition = (contentDisposition: string | undefined, fallbackName: string): string => {
  if (!contentDisposition) return fallbackName;

  // Supports both filename="foo" and RFC5987 filename*=UTF-8''foo
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
  const simpleMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
  const rawFilename = utf8Match?.[1] || simpleMatch?.[1];
  if (!rawFilename) return fallbackName;

  try {
    return decodeURIComponent(rawFilename).replace(/^"|"$/g, '');
  } catch (_) {
    return rawFilename.replace(/^"|"$/g, '');
  }
};

const _saveBlob = (blob: Blob, filename: string): { success: boolean; filename?: string; error?: any } => {
  try {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
    return { success: true, filename };
  } catch (error) {
    return { success: false, error };
  }
};

export default DialogService;

export { DialogService };
